import * as React from 'react';
import Cropper from 'react-easy-crop';
import Modal from 'react-modal';

import {proporcaoValida} from './imageCrop';
import {useImageCropModal} from './ImageCropModal.use-case';

if (typeof window !== 'undefined') {
  Modal.setAppElement(document.body);
}

// O tema do admin pinta titulo e icone de branco; dentro do modal, que e claro,
// isso deixaria o texto invisivel.
const COR_TEXTO = '#212121';

// A pagina de cadastro nao carrega Materialize nem Material Icons: `.btn`,
// `.btn-flat` e <i class="material-icons"> ficariam sem estilo e sem glifo.
const botaoBase: React.CSSProperties = {
  border: 0,
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 600,
  padding: '10px 18px',
};

const botaoSecundario: React.CSSProperties = {
  ...botaoBase,
  background: 'transparent',
  color: COR_TEXTO,
};

const botaoPrimario: React.CSSProperties = {
  ...botaoBase,
  background: '#006bf3',
  color: '#fff',
};

const IconeFechar = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconeZoom = ({mais}: {mais: boolean}) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
    <path d="M20 20l-3.5-3.5M8 11h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    {mais && <path d="M11 8v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />}
  </svg>
);

const modalStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    zIndex: 9999,
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '640px',
    width: '90%',
    padding: '20px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    color: COR_TEXTO,
  },
};

export interface ImageCropModalProps {
  arquivo: File;
  /** 1 para avatar. Sem valor, o recorte fica livre. */
  proporcao?: number;
  titulo?: string;
  onCancel: () => void;
  onConfirm: (arquivo: File) => void;
}

export const ImageCropModal = ({
  arquivo,
  proporcao,
  titulo = 'Ajustar imagem',
  onCancel,
  onConfirm,
}: ImageCropModalProps): JSX.Element => {
  const _useCase = useImageCropModal({arquivo, onConfirm});
  const exigida = proporcaoValida(proporcao);
  const aspecto = exigida ?? _useCase.aspectoNatural ?? undefined;
  const redondo = aspecto === 1;

  return (
    <Modal
      isOpen={true}
      onRequestClose={onCancel}
      style={modalStyles}
      contentLabel={titulo}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={true}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}>
        <h5 style={{margin: 0, color: COR_TEXTO}}>{titulo}</h5>
        <button
          type="button"
          onClick={onCancel}
          disabled={_useCase.processando}
          style={{...botaoSecundario, padding: 4, lineHeight: 0}}
          aria-label="Fechar"
          title="Cancelar">
          <IconeFechar />
        </button>
      </div>

      {_useCase.erro && (
        <div
          style={{
            marginBottom: '16px',
            padding: '10px 14px',
            borderRadius: 8,
            background: '#fdecea',
            color: '#c20808',
            fontSize: 14,
          }}
          role="alert">
          {_useCase.erro}
        </div>
      )}

      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '320px',
          background: '#333',
          borderRadius: '4px',
          overflow: 'hidden',
        }}>
        {aspecto === undefined ? (
          <p style={{color: '#fff', textAlign: 'center', paddingTop: '140px'}}>Carregando…</p>
        ) : (
        <Cropper
          image={_useCase.urlOrigem}
          crop={_useCase.posicao}
          zoom={_useCase.zoom}
          aspect={aspecto}
          cropShape={redondo ? 'round' : 'rect'}
          showGrid={!redondo}
          restrictPosition={true}
          onCropChange={_useCase.setPosicao}
          onZoomChange={_useCase.setZoom}
          onCropComplete={_useCase.aoTerminarRecorte}
        />
        )}
      </div>

      <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginTop: '16px'}}>
        <span style={{color: COR_TEXTO, lineHeight: 0}}>
          <IconeZoom mais={false} />
        </span>
        <input
          type="range"
          min={1}
          max={3}
          step={0.01}
          value={_useCase.zoom}
          onChange={(evento) => _useCase.setZoom(Number(evento.target.value))}
          aria-label="Aproximar imagem"
          style={{flex: 1}}
        />
        <span style={{color: COR_TEXTO, lineHeight: 0}}>
          <IconeZoom mais />
        </span>
      </div>

      <div style={{display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px'}}>
        <button
          type="button"
          onClick={onCancel}
          style={botaoSecundario}
          disabled={_useCase.processando}>
          Cancelar
        </button>
        <button
          type="button"
          onClick={_useCase.confirmar}
          style={{...botaoPrimario, opacity: _useCase.podeConfirmar ? 1 : 0.6}}
          disabled={!_useCase.podeConfirmar}>
          {_useCase.processando ? 'Recortando…' : 'Usar imagem'}
        </button>
      </div>
    </Modal>
  );
};
